// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
// import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table"; // Store Custom Code
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { hideUnder, mergeCssToProps } from "@store/Themes/common/props/table";
import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    // Store Custom Code
    responsiveTableCells?: DataTableCellProps;
    infoRow?: InjectableCss;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    gridItemResponsive?: GridItemProps;
    gridItemShipTo?: GridItemProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    /* Store Custom Code - Start */
    shipToCell: mergeCssToProps(tableGetEllipsisCellProps(290), hideUnder("lg")),
    customerPOCell: mergeCssToProps(tableGetEllipsisCellProps(95), hideUnder("lg")),
    statusCell: mergeCssToProps(tableGetEllipsisCellProps(95), hideUnder("lg")),
    totalCell: mergeCssToProps(tableProps.amountCell, hideUnder("lg")),
    /* Store Custom Code - End */
    // Store Custom Code
    dateCell: hideUnder("lg"),
    orderNumberCell: hideUnder("lg"),
    responsiveTableCells: tableProps.responsiveTableCells,
    infoRow: tableProps.infoRow,
    infoLabel: tableProps.infoLabel,
    infoValue: tableProps.infoValue,
    gridItemShipTo: {
        css: css`
            display: flex;
            flex-direction: column;
            white-space: normal;
        `,
    },
    gridItemResponsive: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    /* Starter Custom Code - Start */

    /* Store Custom Code - Start */
    const orderNumber = orderSettings.showWebOrderNumber
        ? order.erpOrderNumber || ""
        : order.erpOrderNumber || order.webOrderNumber;
    const { webOrderNumber } = order;

    const getMobileTableCell = (
        linkOrderNumber: string,
        date: string,
        orderNumber: string,
        shipTo: string,
        status: string,
        po: string,
        total: string,
        webOrderNumber: string,
    ) => {
        return (
            <DataTableCell {...styles.responsiveTableCells}>
                <GridContainer gap={10}>
                    <GridItem width={12} {...styles.gridItemResponsive}>
                        <OrderDetailPageTypeLink title={date} orderNumber={linkOrderNumber} />
                    </GridItem>
                    <GridItem width={[12, 0, 0, 0, 0]} {...styles.gridItemShipTo}>
                        <Typography {...styles.infoLabel}>
                            {translate("Ship To / Pick Up", "stCompanyName")}
                            :&nbsp;
                        </Typography>
                        {shipTo}
                    </GridItem>
                    <GridItem {...styles.gridItemResponsive} width={[12, 5, 5, 3, 0] as any}>
                        <GridContainer gap={10}>
                            <GridItem {...styles.gridItemResponsive} width={12}>
                                <StyledWrapper {...styles.infoRow}>
                                    <Typography {...styles.infoLabel}>{translate("Order #")}:&nbsp;</Typography>
                                    <Typography {...styles.infoValue}>
                                        {orderNumber ? (
                                            <OrderDetailPageTypeLink
                                                title={orderNumber || " "} // Store Custom Code
                                                orderNumber={linkOrderNumber}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </Typography>
                                </StyledWrapper>
                                {orderSettings.showWebOrderNumber && (
                                    <StyledWrapper {...styles.infoRow}>
                                        <Typography {...styles.infoLabel}>{translate("Web Order #")}:&nbsp;</Typography>
                                        <Typography {...styles.infoValue}>
                                            {webOrderNumber ? (
                                                <OrderDetailPageTypeLink
                                                    title={webOrderNumber} // Store Custom Code
                                                    orderNumber={linkOrderNumber}
                                                />
                                            ) : (
                                                <Typography>{translate("N/A")}</Typography>
                                            )}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.infoRow}>
                                    <Typography {...styles.infoLabel}>
                                        {translate("PO #", "customerPO")}:&nbsp;
                                    </Typography>
                                    <Typography {...styles.infoValue}>{po}</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.infoRow}>
                                    <Typography {...styles.infoLabel}>{translate("Status")}:&nbsp;</Typography>
                                    <Typography {...styles.infoValue}>{status}</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.infoRow}>
                                    <Typography {...styles.infoLabel}>
                                        {translate("Total", "orderTotal")}
                                        :&nbsp;
                                    </Typography>
                                    <Typography {...styles.infoValue}>{total}</Typography>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.gridItemResponsive} width={[0, 7, 7, 9, 0] as any}>
                        <StyledWrapper {...styles.gridItemShipTo}>
                            <Typography {...styles.infoLabel}>
                                {translate("Ship To / Pick Up", "stCompanyName")}
                                :&nbsp;
                            </Typography>
                            {shipTo}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </DataTableCell>
        );
    };
    /* Store Custom Code - End */

    const shipTo = `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;

    return (
        <DataTableRow key={order.id}>
            {/* Store Custom Code - cells reordered - Start */}
            {getMobileTableCell(
                orderNumber,
                orderDateDisplay,
                orderNumber,
                shipTo,
                order.statusDisplay,
                order.customerPO,
                order.orderGrandTotalDisplay,
                webOrderNumber,
            )}
            <DataTableCell {...styles.dateCell} data-test-selector="orderSummaryCard_Date">
                <OrderDetailPageTypeLink title={orderDateDisplay} orderNumber={orderNumber || webOrderNumber} />
            </DataTableCell>
            <DataTableCell {...styles.orderNumberCell} data-test-selector={`orderSummaryCard_${orderNumber}`}>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
            <DataTableCell {...styles.statusCell}>{order.statusDisplay}</DataTableCell>
            {/* Store Custom Code - Start */}
            {orderSettings.showWebOrderNumber && (
                <DataTableCell {...styles.orderNumberCell}>
                    {webOrderNumber ? (
                        <OrderDetailPageTypeLink title={webOrderNumber} orderNumber={webOrderNumber} />
                    ) : (
                        <Typography>{translate("N/A")}</Typography>
                    )}
                </DataTableCell>
            )}
            {/* Store Custom Code - cells reordered - End */}
            <DataTableCell {...styles.customerPOCell}>{order.customerPO}</DataTableCell>
            <DataTableCell {...styles.totalCell}>{order.orderGrandTotalDisplay}</DataTableCell>
            {/* Store Custom Code - End */}
        </DataTableRow>
    );
    /* Starter Custom Code - End */
};

export default connect(mapStateToProps)(OrderSummaryCard);
